<template>
  <div>
    <Spinner v-if="loading" />
    <section v-else>
      <v-toolbar flat dense class="mb-4">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search drivers, trip #, notes..."
          single-line
          v-bind="inputProps"
          dense
          clearable
          style="max-width: 400px"
          class="align-self-center"
          @click:clear="handleSearchClear"
        />

        <v-spacer />

        <v-switch
          v-model="expandAll"
          :label="`Expand All (${expanded.length}/${filteredLogs.length})`"
          inset
          dense
          class="mr-4 align-self-center"
          @change="toggleExpandAll"
          hide-details="auto"
        >
          <template v-slot:label>
            <div class="d-flex flex-column">
              <span>Expand All</span>
              <span class="caption">({{ expanded.length }}/{{ filteredLogs.length }})</span>
            </div>
          </template>
        </v-switch>

        <!-- Hiding this for now until we have the feature to run process in the background -->
        <!-- <v-btn
          color="primary"
          outlined
          @click="exportDriverLogs"
          :disabled="!canExport"
          class="align-self-center"
          :loading="isExporting"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export
        </v-btn> -->
      </v-toolbar>

      <v-sheet elevation="1" class="my-4 pa-4 rounded">
        <v-row align="center" no-gutters>
          <v-col cols="12" md="8">
            <v-autocomplete
              v-model="selectedDrivers"
              :items="drivers"
              item-text="fullName"
              item-value="id"
              label="Filter by Drivers"
              v-bind="inputProps"
              dense
              clearable
              multiple
              class="mt-2"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < DRIVER_CHIP_DISPLAY_LIMIT" small close @click:close="removeDriver(item.id)">
                  {{ item.fullName }}
                </v-chip>
                <span v-if="index === DRIVER_CHIP_DISPLAY_LIMIT && selectedDrivers.length > 3">
                  +{{ selectedDrivers.length - DRIVER_CHIP_DISPLAY_LIMIT }} more
                </span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="4" class="d-flex align-center justify-end mt-2 mt-md-0">
            <v-switch
              v-model="allDriversSelected"
              :label="`${allDriversSelected ? 'Clear' : 'Select'} All Drivers`"
              hide-details
              dense
              inset
              @change="toggleDriverSelect"
            />
          </v-col>
        </v-row>
      </v-sheet>

      <v-data-table
        ref="dataTable"
        @update:options="updateSort"
        :headers="headers"
        :items="filteredLogs"
        :search="search"
        :expanded.sync="expanded"
        show-expand
        item-key="id"
        class="elevation-1"
        @update:expanded="checkExpandState"
      >
        <template v-slot:expanded-item="{ item }">
          <td colspan="100%" class="pa-4" style="background: #f5f5f5">
            <div class="text-left">
              <strong>Details:</strong>
              <p class="mt-2">{{ item.message }}</p>
            </div>
          </td>
        </template>

        <template v-slot:item.message="{ item }">
          <span :title="item.message">
            {{
              item.message.length > MESSAGE_LENGTH ? item.message.substring(0, MESSAGE_LENGTH) + '...' : item.message
            }}
          </span>
        </template>

        <template v-slot:item.timestamp="{ item }">
          {{ toDateString(new Date(item.timestamp * 1000)) }}
        </template>
      </v-data-table>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { toDateString, inputProps } from '@/util';
import Spinner from '../shared/Spinner.vue';
const MESSAGE_LENGTH = 60;
const DRIVER_CHIP_DISPLAY_LIMIT = 5;

export default {
  name: 'DriverLog',
  inject: ['eventHub'],
  components: { Spinner },
  data() {
    return {
      DRIVER_CHIP_DISPLAY_LIMIT,
      MESSAGE_LENGTH,
      search: '',
      expanded: [],
      expandAll: false,
      selectedDrivers: [],
      isExporting: false,
      loading: false,
      sortBy: 'timestamp',
      sortDesc: false,
      headers: [
        { text: 'Logged By', value: 'loggedBy', sortable: true },
        { text: 'Driver Name', value: 'driver', sortable: true },
        { text: 'Trip Number', value: 'tripId', sortable: true, align: 'end' },
        { text: 'Log Date', value: 'timestamp', sortable: true },
        { text: 'Action', value: 'message', sortable: false },
      ],
      inputProps,
      allDriversSelected: false,
    };
  },
  computed: {
    ...mapGetters('driverLog', ['driverLogs']),
    ...mapGetters('driver', ['drivers', 'driversById']),
    canExport() {
      return this.filteredLogIds.length > 0;
    },
    drivers() {
      return Object.keys(this.driverLogs).reduce((acc, id) => {
        const driver = this.driversById[id];
        if (driver) acc.push(driver);
        return acc;
      }, []);
    },
    logs() {
      return Object.keys(this.driverLogs)
        .filter((driverId) => this.selectedDrivers.includes(Number(driverId)))
        .reduce((filteredLogs, driverId) => {
          filteredLogs[driverId] = this.driverLogs[driverId].map((log) => {
            const tripMatch = log.message.match(/Trip #\d+/);
            return {
              ...log,
              driver: this.driversById[driverId].fullName,
              tripId: tripMatch ? Number(tripMatch[0].split('#')[1]) : null,
            };
          });
          return filteredLogs;
        }, {});
    },
    filteredLogs() {
      return Object.values(this.logs)
        .flat()
        .filter(
          (log) =>
            log.loggedBy.toLowerCase().includes(this.search.toLowerCase()) ||
            log.message.toLowerCase().includes(this.search.toLowerCase()) ||
            log.tripId?.toString().includes(this.search)
        );
    },
    filteredLogIds() {
      return [...this.filteredLogs]
        .sort((a, b) => {
          if (!this.sortBy) return 0;
          return this.sortDesc ? b[this.sortBy] - a[this.sortBy] : a[this.sortBy] - b[this.sortBy];
        })
        .map((log) => log.id);
    },
  },
  methods: {
    ...mapActions('driverLog', ['getDriverLogs']),
    toDateString,
    removeDriver(id) {
      const numericId = Number(id);
      this.selectedDrivers = this.selectedDrivers.filter((driverId) => Number(driverId) !== numericId);
    },
    updateSort(options) {
      this.sortBy = options.sortBy?.[0] || 'timestamp';
      this.sortDesc = options.sortDesc?.[0] || false;
    },
    exportDriverLogs() {
      this.isExporting = true;
      setTimeout(() => {
        console.log('Filtered Log IDs:', this.filteredLogIds);
        this.isExporting = false;
      }, 1000);
    },
    selectAllDrivers() {
      this.selectedDrivers = this.drivers.map((d) => d.id);
    },
    toggleExpandAll() {
      this.expanded = this.expandAll ? this.filteredLogs : [];
    },
    checkExpandState(value) {
      this.expanded = value;
      this.expandAll = this.expanded.length > 0 && this.expanded.length === this.filteredLogs.length;
    },
    handleSearchClear() {
      this.search = '';
    },
    toggleDriverSelect() {
      if (this.allDriversSelected) this.selectAllDrivers();
      else this.selectedDrivers = [];
    },
  },
  watch: {
    selectedDrivers(val) {
      this.allDriversSelected = val.length === this.drivers.length;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      await this.getDriverLogs();
    } catch (e) {
      this.$myalert.error(e.message, true);
    } finally {
      this.loading = false;
    }
  },
};
</script>
